/*
 * QRCode
 * @module components
 * @flow
 */

import React, { Fragment } from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';
// $FlowIssue
import { FormattedMessage } from 'react-intl';

import { QRCodeImage } from '~/components/QRCodeImage';
import { Modal, StyledButton } from '~/components';
// $FlowIssue
import { ReactComponent as IconCode } from '@/icons/qr-code.svg';

import messages from './messages';

export type QRCodeProps = {
  value: string,
  palette?: string,
  isVisible?: boolean,
  className?: string,
  onToggle?: () => void,
  device: $PropertyType<DeviceQuery, 'device'>,
};

const QRCodeButton = styled.button.attrs(() => ({
  className: 'react-qr-button',
}))`
  display: flex;
  align-items: center;
  border-radius: 20px;
  background-color: ${palette(1)};
  color: #fff;
  padding: 8px 16px;
  margin-left: 8px;
  border: none;
  cursor: pointer;
`;

const QRCodeLabel = styled.span`
  font-size: 15px;
  line-height: 1.6;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  color: inherit;
  text-transform: uppercase;
`;

const QRCodeIcon = styled(IconCode).attrs(() => ({
  className: 'react-qr-icon',
}))`
  margin-right: 8px;

  ${ifProp(
    { device: 'mobile' },
    css`
      width: 18px;
      height: 18px;
    `,
  )};
`;

const QRCodeContentWrapper = styled.div`
  width: 100%;
  height: 100%;

  ${ifProp(
    { device: 'mobile' },
    css`
      box-sizing: border-box;
      padding: 22px 3px 0;
      text-align: center;
    `,
    css`
      display: flex;
      align-items: center;
      flex-direction: column;
    `,
  )};
`;

const QRCodeHeader = styled.h4`
  ${ifProp(
    { device: 'mobile' },
    css`
      margin-bottom: 60px !important;
    `,
  )};
`;

const QRCodeImageWrapper = styled(QRCodeImage)`
  margin: 17px 0 26px;

  ${ifProp(
    { device: 'mobile' },
    css`
      width: 100%;
      height: auto;

      svg {
        width: 100%;
        height: 100%;
      }
    `,
  )};
`;

const QRCodeCloseButton = styled(StyledButton)`
  position: absolute;
  left: 50%;
  bottom: 20px;
  min-width: 100px;
  transform: translateX(-50%);
`;

/**
 * QRCode React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Node<*>} [props.button] - button
 * @param {string} [props.value] - string to encode
 * @param {string} [props.palette = 'primary'] - current QR Code palette
 * @param {string} [props.className] - QR Code button class name
 * @param {boolean} [props.isVisible] - show state of QR Code image
 * @param {Function} [props.onToggle = () => {}] - on toggle QRCode image callback
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device
 * @example <caption>Simple usage</caption>
 * <QRCode value="http://facebook.github.io/react" />
 * @returns {React.Node<*>} React-component
 */
export const QRCode = ({
  button,
  value,
  palette,
  className,
  isVisible,
  onToggle,
  device,
}: QRCodeProps) => {
  const isMobile = device !== 'desktop';

  return value ? (
    <Fragment>
      <div onClick={onToggle} testid="qrcode">
        {button || (
          <QRCodeButton palette={palette}>
            <QRCodeIcon className={className} device={device} />
            <QRCodeLabel>
              <FormattedMessage {...messages.buttonLabel} />
            </QRCodeLabel>
          </QRCodeButton>
        )}
      </div>
      <Modal.Portal
        onClose={onToggle}
        isVisible={isVisible}
        fullscreen={isMobile}
      >
        <QRCodeContentWrapper device={device}>
          <QRCodeHeader device={device}>
            {<FormattedMessage {...messages.title} />}
          </QRCodeHeader>
          <QRCodeImageWrapper value={value} device={device} />
          {isMobile && (
            <QRCodeCloseButton palette="primary" onClick={onToggle} small>
              <FormattedMessage {...messages.closeButtonLabel} />
            </QRCodeCloseButton>
          )}
        </QRCodeContentWrapper>
      </Modal.Portal>
    </Fragment>
  ) : null;
};

QRCode.defaultProps = {
  palette: 'primary',
  onToggle: () => {},
};
